/**
 * FirmaTaleplerListPage.jsx — Talepler Listesi (Dekoratör görünümü, kart görünümü)
 * Route: /Firma/Talepler
 *
 * Bu firmanın TÜM standlarının, her biri tek bir karta özetlenmiş listesi —
 * firmaTalepListService.getFirmaTalepListesi standOverviewService'in stand
 * roster'ını standTalepService'in dört alt kaynağıyla (Teknik/Hizmet/İş İzin/
 * Ekipman) süsler. Görsel dil /fromImageToLayout ile StandListPage.jsx'ten
 * (Management/Projects) alındı: koyu salon/stand rozeti, ikon-kare satırı, gri
 * ayraçlarla gruplanmış kategoriler, sağda yığılmış onay-durumu sayaçları,
 * "Detaylar" pill butonu. Salt okunur: talep oluşturma/düzenleme yok, satır
 * aksiyonu FirmaTalepStandDetailPage.jsx'e (/Firma/Talepler/:standId — Teknik
 * Talepler/Hizmet Talepleri/İş İzin Talepleri/Ekipman Talepleri/Talepler Borç
 * Durumu sekmelerini taşıyan kardeş sayfa) yönlendirir.
 *
 * 2026-07-23 revizyonu: ikon-kare talep kutucukları (BlankTile hariç) artık
 * tıklanabilir, `?tab=<TABS anahtarı>&kategori=<categoryKey>` ile ilgili
 * sekme/kategoriye yönlendirir (bkz. StandTalepTiles/goTo) — aynı desen
 * FirmaPersonelListPage.jsx'teki StatusTile'da.
 *
 * 2026-11-02 (#696, backend #695): the rule below is GONE. Every stand is a normal card, with or without a proje beyanı (`projectDeclared` no longer
 * gates anything here): the company starts its requests early. A Teknik request saved but not sent for approval (a draft, `taslak`) is drawn as a
 * dashed square (see TalepTile); Hizmet / İş İzni / Ekipman are created as before. The note that follows is kept as history.
 *
 * 2026-07-23 revizyonu (yeni kural): proje bilgisi henüz yüklenmemiş bir stand
 * (standOverviewService'teki projectDeclared:false, örn. standId 326 — Projeler
 * sayfasındaki "Proje Yükle" akışının aynı demo kaydı) için talep oluşturulamaz.
 * Bu standlar normal kutucuk satırı yerine kilitli bir "önce proje yükleyin"
 * satırı gösterir (bkz. NoProjectRow) — FirmaProjelerListPage.jsx'teki uyarı
 * kartıyla aynı görsel dil, ama aksiyon burada devre dışı bir buton (Talepler
 * sayfası salt okunur, gerçek oluşturma FirmaTalepStandDetailPage.jsx'teki "+"
 * butonlarında).
 *
 * 2026-07-31 revizyonu: stand rozetinin (StandThumbnail) arka planı artık sabit
 * koyu gri değil — "Talepler Borç Durumu" sekmesindeki (FirmaTalepStandDetailPage.jsx,
 * BorcDurumuPanel) Alan/Teknik/Ekipman kategorilerinin toplu rengini taşır (bkz.
 * talepBorcRenk). Bu üç kalem standTalepService'te toplu döndürülmediği için
 * records yüklendikten sonra ayrı bir Promise.all effect'iyle (borcByStandId)
 * çekilir — teknikByStandId (FirmaStandListPage.jsx) ile aynı desen.
 *
 * 2026-08-10 revizyonu (mock→backend reconciliation): roster fetch'i artık
 * selectedFuarId seed edilmiş test fuarlarından (298/299, bkz.
 * docs/guides/FE-README-guide.md "Known-good test data") biriyse
 * firmaTalepListService.getFirmaTalepListesi'ye forceLive:true geçiyor — StandOverviewDetailPage.jsx'teki
 * standId-bazlı forceLive tetikleyicisiyle aynı desen (util/knowledgebase/local-backend-testing.md),
 * ama roster fetch'inin kendisinde henüz bir standId olmadığından sinyal standId
 * yerine fuarId'den geliyor. Bu moddayken decoratorFirmaId de gerçek dekoratöre
 * (TEST_DECORATOR_FIRMA_ID) sabitlenir — localStorage'daki mock user.firmaId
 * gerçek backend'de var olmayan bir firma. Roster canlı geldikten sonra her
 * kaydın standId'si gerçek sayısal id olduğundan, borcByStandId effect'i aşağıda
 * standId bazında (isNumericStandId) forceLive'a karar verir — fuarId sinyaline
 * ihtiyaç duymaz, StandOverviewDetailPage.jsx'teki asıl desenle birebir aynı.
 */
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container, Card, CardBody, Row, Col, Input, Button, Spinner, Alert,
  Pagination, PaginationItem, PaginationLink,
} from "reactstrap";
import {
  Eye, RefreshCw, ClipboardList, Clock,
  Sparkles, Users, Shield,
  Cog, PartyPopper, CookingPot, Waves, Flame, PawPrint, Scissors, Crosshair,
  Armchair,
} from "lucide-react";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import DurumSayaclari from "../../Components/Common/DurumSayaclari";
import MainContext from "../../Components/Contexts/MainContext";
import { TEKNIK_ICONS } from "../../Components/Projects/StandPills";
// WAS (until #403): import { getFirmaTalepListesi, TEST_DECORATOR_FIRMA_ID } from "../../services/firmaTalepListService";
import { getFirmaTalepListesi } from "../../services/firmaTalepListService";
// #403: the live branch takes the logged-in company's id from the portal context (GET /portal/context).
import { usePortal } from "../../Components/Contexts/PortalContext";
import {
  TEKNIK_TALEP_KATEGORILERI, HIZMET_TALEP_KATEGORILERI, IS_IZNI_KATEGORILERI, TALEP_STATUS,
  getBorcDurumu, BORC_DURUMU_KATEGORILERI, BORC_DURUMU_STATUS,
} from "../../services/standTalepService";
import { MOCK_DECORATOR_FIRMA_ID } from "../../services/firmaProfileService";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

function getCurrentFirmaId() {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    return user?.firmaId ?? MOCK_DECORATOR_FIRMA_ID;
  } catch {
    return MOCK_DECORATOR_FIRMA_ID;
  }
}

// forceLive köprüsünü hangi fuarlar için açacağımız — StandOverviewDetailPage.jsx'teki
// isNumericStandId'nin standId yerine fuarId üzerinden çalışan karşılığı (bkz. dosya
// başı 2026-08-10 notu, roster fetch'inde henüz bir standId yok). Dosya-yerel kopya.
// WAS (until #398): only the two seeded test fairs ran on the real backend, every other fair fell back to mock data:
// const LIVE_TEST_FUAR_IDS = [298, 299];
// const isLiveTestFuarId = (fuarId) => LIVE_TEST_FUAR_IDS.includes(Number(fuarId));
// #398: a full-simulation test creates a NEW fair, so every real fair id is live.
const isLiveTestFuarId = (fuarId) => Number(fuarId) > 0;

// StandOverviewDetailPage.jsx'teki aynı isimli helper'ın dosya-yerel kopyası (bkz. o
// dosyadaki 2026-08-10 notu) — borcByStandId effect'i aşağıda standId bazında karar
// vermek için kullanır.
const isNumericStandId = (v) => /^\d+$/.test(String(v));

const HIZMET_ICONS = { "24saatElektrik": Clock, temizlik: Sparkles, hostHostes: Users, guvenlik: Shield };
const IS_IZNI_ICONS = {
  makine: Cog, gosteri: PartyPopper, pisirme: CookingPot, sivi: Waves,
  sicak: Flame, hayvan: PawPrint, kesici: Scissors, silah: Crosshair,
};

// Renkler standTalepService.js'teki gibi sabit hex — bu temada çalışma zamanı
// `--bs-*` CSS değişkenleri yok (bkz. o dosyadaki yorum).
const TALEP_GRAY = "#878a99";

// Dikey ayraç — bootstrap'ın .vr utility sınıfı bu temanın derlenmiş CSS'inde
// width:0 hesaplıyor, o yüzden class yerine inline stil kullanılıyor (bkz. aynı
// çözüm StandListPage.jsx'teki VDivider'da).
function VDivider() {
  return (
    <div
      className="d-none d-md-block"
      style={{ width: 1, alignSelf: "stretch", backgroundColor: "currentColor", opacity: 0.15 }}
    />
  );
}

// onClick verildiğinde kare tıklanabilir olur — FirmaTalepStandDetailPage.jsx'teki
// ilgili sekme/kategoriye yönlendirir (bkz. StandTalepTiles/goTo, aynı desen
// FirmaPersonelListPage.jsx'teki StatusTile'da).
// #696: `taslak` = a Teknik request saved but not sent for approval yet (backend #695): an empty square with a dashed outline in the category's colour,
// so it reads as "started" and differs from both the filled (requested) and the grey faded (not requested) ones.
function TalepTile({ Icon, color, opacity = 1, label, onClick, taslak = false }) {
  const interactiveProps = onClick
    ? {
        role: "button",
        tabIndex: 0,
        onClick,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        },
      }
    : {};
  return (
    <div
      className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
      style={{
        width: 40, height: 40, opacity, cursor: onClick ? "pointer" : undefined,
        ...(taslak ? { background: "transparent", border: `2px dashed ${color}` } : { background: color }),
      }}
      title={label}
      data-taslak={taslak ? "true" : undefined}
      {...interactiveProps}
    >
      <Icon size={18} color={taslak ? color : "#fff"} />
    </div>
  );
}

// Boş rezerve slot — TalepTile ile aynı ölçüde ama görünmez, grup genişliğini
// (kaç talep olursa olsun) sabit tutmak için kullanılır. Veri taşımadığı için
// tıklanabilir değildir.
function BlankTile() {
  return <div className="flex-shrink-0" style={{ width: 40, height: 40 }} />;
}

// Rezerve slot sayısını aşan talepler için küçük "+N" rozeti — kapanan kalemler
// sadece "Detaylar" sayfasında (FirmaTalepStandDetailPage) görülebilir; onClick
// verildiğinde ilgili sekmeye (kategori belirtmeden) yönlendirir.
function OverflowBadge({ count, onClick }) {
  const { t } = useTranslation("portal");
  const interactiveProps = onClick
    ? {
        role: "button",
        tabIndex: 0,
        onClick,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        },
      }
    : {};
  return (
    <div
      className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0 text-muted small fw-semibold"
      style={{ width: 40, height: 40, background: "rgba(135, 138, 153, 0.12)", cursor: onClick ? "pointer" : undefined }}
      title={t("talepler.talepDahaDetaylarDaGoruntulenebilir", { count })}
      {...interactiveProps}
    >
      +{count}
    </div>
  );
}

// Hizmet/İş İzin/Ekipman'ın liste kartındaki önizleme genişliği — gerçek kategori
// sayısından (sırasıyla 4/8/1) bağımsız, sabit bir kart-içi rezerve slot sayısı.
// Taşan talepler OverflowBadge ile "+N" olarak özetlenir, tam liste detay sayfasında.
const HIZMET_CAP = 2;
const IS_IZNI_CAP = 2;
const EKIPMAN_CAP = 1;

function buildGroupSlots(tiles, cap) {
  const shown = tiles.slice(0, cap);
  const blankCount = Math.max(0, cap - shown.length);
  const overflow = Math.max(0, tiles.length - cap);
  return { shown, blankCount, overflow };
}

// Teknik Talepler her zaman 4'ü de gösterilir (talep edilmemişse gri). Hizmet/İş
// İzin/Ekipman her biri sabit genişlikte bir rezerve alan gösterir (bkz. HIZMET_CAP
// vd.) — talep sayısı kadar dolu kutu, kalanı boş slot, taşarsa "+N" rozeti; talep
// hiç yoksa da alan + ayraç yine görünür kalır, böylece her satır aynı genişlikte olur.
function StandTalepTiles({ record }) {
  const { t: tx } = useTranslation("portal");
  const navigate = useNavigate();

  // FirmaTalepStandDetailPage.jsx'in TABS anahtarlarıyla eşleşen hedef sekmeye
  // (ve varsa o sekmedeki kategori araç çubuğu satırına) yönlendirir.
  function goTo(tab, kategori) {
    const query = kategori ? `?tab=${tab}&kategori=${kategori}` : `?tab=${tab}`;
    navigate(`/Firma/Talepler/${record.standId}${query}`);
  }

  const teknikTiles = record.teknik.map((r) => {
    const cat = TEKNIK_TALEP_KATEGORILERI.find((c) => c.key === r.categoryKey);
    return {
      id: r.categoryKey, Icon: TEKNIK_ICONS[r.categoryKey],
      color: r.hasRequest ? cat.color : TALEP_GRAY, opacity: r.hasRequest ? 1 : 0.55,
      // #696: a draft is drawn dashed and says so in its tooltip
      taslak: !!(r.hasRequest && r.taslak),
      label: r.hasRequest ? (r.taslak ? tx("talepler.taslakTalep", { label: cat.label }) : cat.label) : tx("talepler.talepEdilmedi", { label: cat.label }),
    };
  });

  const hizmetTiles = record.hizmet.map((r) => {
    const cat = HIZMET_TALEP_KATEGORILERI.find((c) => c.key === r.categoryKey);
    return { id: r.categoryKey, Icon: HIZMET_ICONS[r.categoryKey], color: cat.color, label: cat.label };
  });

  const isIzinTiles = record.isIzin.map((r) => {
    const cat = IS_IZNI_KATEGORILERI.find((c) => c.key === r.categoryKey);
    const statusInfo = TALEP_STATUS[r.status] ?? TALEP_STATUS[0];
    return { id: r.categoryKey, Icon: IS_IZNI_ICONS[r.categoryKey], color: statusInfo.hex, label: `${cat.label} — ${statusInfo.label}` };
  });

  const ekipmanTiles = record.ekipman ? [(() => {
    const statusInfo = TALEP_STATUS[record.ekipman.status] ?? TALEP_STATUS[0];
    return { id: "ekipman", Icon: Armchair, color: statusInfo.hex, label: tx("talepler.ekipman", { label: statusInfo.label }) };
  })()] : [];

  const groups = [
    { ...buildGroupSlots(hizmetTiles, HIZMET_CAP), tabKey: "hizmetTalepleri" },
    { ...buildGroupSlots(isIzinTiles, IS_IZNI_CAP), tabKey: "isIzinTalepleri" },
    { ...buildGroupSlots(ekipmanTiles, EKIPMAN_CAP), tabKey: "ekipmanTalepleri" },
  ];

  return (
    <div className="d-flex align-items-center flex-wrap gap-2">
      {teknikTiles.map((t) => (
        <TalepTile key={t.id} {...t} onClick={() => goTo("teknikTalepler", t.id)} />
      ))}
      {groups.map((group, i) => (
        <React.Fragment key={i}>
          <VDivider />
          {group.shown.map((t) => (
            <TalepTile key={t.id} {...t} onClick={() => goTo(group.tabKey, t.id)} />
          ))}
          {Array.from({ length: group.blankCount }, (_, bi) => <BlankTile key={`blank-${i}-${bi}`} />)}
          {group.overflow > 0 && (
            <OverflowBadge key={`overflow-${i}`} count={group.overflow} onClick={() => goTo(group.tabKey)} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// "Talepler Borç Durumu" sekmesindeki (FirmaTalepStandDetailPage.jsx, BorcDurumuPanel)
// 3 kategorinin (Alan/Teknik/Ekipman — Hizmet Borcu kasıtlı olarak hariç, bkz.
// standTalepService.js'teki HIZMET_BORC_META yorumu) toplu rengi: herhangi biri
// "bekliyor" ise kırmızı, aksi halde (hepsi ödendi/yok) teal. Veri yoksa nötr gri.
function talepBorcRenk(borcItems) {
  if (!borcItems) return TALEP_GRAY;
  const relevant = borcItems.filter((i) => BORC_DURUMU_KATEGORILERI.some((c) => c.key === i.categoryKey));
  if (relevant.length === 0) return TALEP_GRAY;
  return relevant.some((i) => i.status === "bekliyor")
    ? BORC_DURUMU_STATUS.bekliyor.hex
    : BORC_DURUMU_STATUS.odendi.hex;
}

// Arka plan artık sabit koyu gri değil — bu standın Talepler Borç Durumu rengini
// taşır (bkz. talepBorcRenk / dosya sonundaki borcByStandId effect'i). Borç
// kaydı henüz gelmemiş standlarda rozet nötr gri kalır.
function StandThumbnail({ salonNumber, standName, color = TALEP_GRAY, onClick }) {
  const { t } = useTranslation("portal");
  const interactiveProps = onClick
    ? {
        role: "button",
        tabIndex: 0,
        title: t("talepler.detaylar"),
        onClick,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        },
      }
    : {};
  return (
    <div
      className="position-relative text-white rounded d-flex flex-column align-items-center justify-content-center flex-shrink-0"
      style={{ width: 64, height: 64, background: color, cursor: onClick ? "pointer" : undefined }}
      {...interactiveProps}
    >
      <span
        className="position-absolute top-0 start-0 translate-middle bg-success text-white rounded-circle d-flex align-items-center justify-content-center"
        style={{ width: 22, height: 22 }}
        title={t("talepler.talepler")}
      >
        <ClipboardList size={12} />
      </span>
      <div className="fw-bold fs-5 lh-1">{salonNumber}</div>
      <div className="small lh-1">{standName}</div>
    </div>
  );
}

// WAS (until #696): NoProjectRow — a stand whose proje beyanı was not made yet (record.projectDeclared === false) was drawn as a locked "declare your
// project first" row instead of its request tiles, with a disabled "Create request" button (rule of 2026-07-23, the same question as #516). Dropped: a
// company can start its requests without the beyan now (Teknik as a draft, backend #695; Hizmet / İş İzni / Ekipman as before), so every stand is a normal card.

// #283: renkli kareler (DurumSayaclari), sıfırlar çizilmez; üçü de standın talep sayfasını YENİ SEKMEDE açar (durum süzgeci yok).
function TalepStatusStack({ counts, href }) {
  const { t } = useTranslation("portal");
  return (
    <DurumSayaclari
      items={[
        { key: "pending", tone: "warning", value: counts?.pending, label: t("talepler.bekleyenTalep"), href },
        { key: "approved", tone: "success", value: counts?.approved, label: t("talepler.onaylananTalep"), href },
        { key: "denied", tone: "danger", value: counts?.denied, label: t("talepler.reddedilenTalep"), href },
      ]}
    />
  );
}

const PAGE_SIZE = 10;

export default function FirmaTaleplerListPage() {
  const { t: tx } = useTranslation("portal");
  const navigate = useNavigate();
  const { selectedFuarId } = useContext(MainContext);
  const portal = usePortal();
  const isLiveTestFuar = isLiveTestFuarId(selectedFuarId);
  // WAS (until #403): TEST_DECORATOR_FIRMA_ID sabit, giriş yapan hesaptan bağımsız — kısa yol, kalıcı
  // değil, bkz. firmaTalepListService.js'teki tanımın yanındaki not ve
  // docs/BACKEND_TODO.md (login gerçek firmaId dönmeye başladığında düzeltilmeli).
  // WAS (until #403): const firmaId = isLiveTestFuar ? TEST_DECORATOR_FIRMA_ID : getCurrentFirmaId();
  // null while /portal/context loads: the fetch effect below waits for it instead of asking for firma "null".
  const firmaId = isLiveTestFuar ? (portal?.firmaId ?? null) : getCurrentFirmaId();

  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [reloadToken, setReloadToken] = useState(0);
  // record.standId → Borç Durumu kayıtları (Alan/Teknik/Ekipman) — standTalepService'te
  // toplu döndüren bir uç yok, bu yüzden records yüklendikten sonra ayrı bir
  // Promise.all effect'iyle çekilir (bkz. FirmaStandListPage.jsx'teki teknikByStandId
  // ile aynı iki aşamalı desen).
  const [borcByStandId, setBorcByStandId] = useState({});

  useEffect(() => { document.title = i18n.t("portal:talepler.taleplerListesiFds"); }, []);

  // KURAL: fetch mantığı bilinçli olarak Effect içinde tanımlanır (üst seviye
  // useCallback olarak paylaşılmaz) — aksi halde react-hooks/set-state-in-effect
  // ihlali oluşur. Yenile butonu reloadToken'ı artırarak tetiklenir.
  useEffect(() => {
    // WAS (until #403): if (!selectedFuarId) return;
    if (!selectedFuarId || !firmaId) return;
    let ignore = false;

    async function run() {
      setLoading(true);
      setError(null);
      try {
        const data = await getFirmaTalepListesi({
          fuarId: selectedFuarId,
          // WAS (until #425): decoratorFirmaId: firmaId,
          // Canlı: GET /stands/talepler/ozet firmaId ile firmanın HER rolde olduğu standları döndürür (backend #423).
          ...(isLiveTestFuar ? { firmaId } : { decoratorFirmaId: firmaId }),
          search,
          page: currentPage,
          limit: PAGE_SIZE,
        }, { forceLive: isLiveTestFuar });
        if (!ignore) {
          setRecords(data.records || []);
          setTotal(data.total || 0);
        }
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    run();
    return () => { ignore = true; };
  }, [selectedFuarId, firmaId, isLiveTestFuar, search, currentPage, reloadToken]);

  useEffect(() => {
    let ignore = false;

    Promise.all(records.map((r) => getBorcDurumu(r.standId, { forceLive: isNumericStandId(r.standId) }).then((data) => [r.standId, data])))
      .then((pairs) => {
        if (!ignore) setBorcByStandId(Object.fromEntries(pairs));
      })
      .catch(() => {
        if (!ignore) setBorcByStandId({});
      });

    return () => { ignore = true; };
  }, [records]);

  function handleSearchChange(e) {
    setSearch(e.target.value);
    setCurrentPage(1);
  }

  function goToStandTalepDetail(record) {
    navigate(`/Firma/Talepler/${record.standId}`);
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("talepler.taleplerListesi")} pageTitle={tx("talepler.firma")} />

        {error && <Alert color="danger">{error}</Alert>}
        {!selectedFuarId && <Alert color="info">{tx("talepler.buSayfayiGormekIcinOnce")}</Alert>}

        {selectedFuarId && (
          <Card className="mb-3" data-tour="talepler-arama">
            <CardBody>
              <Row className="align-items-center g-2">
                <Col md="8">
                  <Input
                    type="text"
                    placeholder={tx("talepler.standSalonVeyaFirmaAra")}
                    value={search}
                    onChange={handleSearchChange}
                    disabled={loading}
                  />
                </Col>
                <Col md="4" className="text-end">
                  <Button
                    color="secondary" outline size="sm"
                    onClick={() => setReloadToken((t) => t + 1)}
                    disabled={loading}
                  >
                    <RefreshCw size={14} className="me-1" />{" "}{tx("common:yenile")}
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        )}

        {loading && (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2">{tx("common:yukleniyor")}</p>
          </div>
        )}

        {!loading && selectedFuarId && records.length === 0 && (
          <Alert color="secondary">{tx("talepler.buFuardaSizeBagliStand")}</Alert>
        )}

        {!loading && records.map((record) => {
          const salonNumber = record.salonName?.match(/\d+/)?.[0] ?? record.salonName;

          return (
            <Card key={record.id} className="mb-3" data-tour="talep-karti">
              <CardBody>
                <Row className="align-items-center g-3">
                  <Col md="auto">
                    <StandThumbnail
                      salonNumber={salonNumber}
                      standName={record.standName}
                      color={talepBorcRenk(borcByStandId[record.standId])}
                      onClick={() => goToStandTalepDetail(record)}
                    />
                  </Col>

                  <Col md={2}>
                    <div className="fw-bold fs-5">{record.firmaName || <span className="text-muted">-</span>}</div>
                    <div className="text-muted small">{record.salonName}</div>
                  </Col>

                  <Col>
                    <StandTalepTiles record={record} />
                  </Col>

                  <Col md="auto" className="d-flex align-items-center gap-3 ms-auto mt-2 mt-md-0">
                    <TalepStatusStack counts={record.statusCounts} href={`/Firma/Talepler/${record.standId}`} />
                    <Button
                      color="info" outline size="sm"
                      onClick={() => goToStandTalepDetail(record)}
                    >
                      <Eye size={14} className="me-1" />{tx("talepler.detaylar")}
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          );
        })}

        {!loading && records.length > 0 && totalPages > 1 && (
          <div className="d-flex justify-content-center p-3">
            <Pagination>
              <PaginationItem disabled={currentPage === 1}>
                <PaginationLink onClick={() => setCurrentPage(1)}>{tx("talepler.ilk")}</PaginationLink>
              </PaginationItem>
              {Array.from({ length: totalPages }, (_, i) => (
                <PaginationItem key={i} active={i + 1 === currentPage}>
                  <PaginationLink onClick={() => setCurrentPage(i + 1)}>{i + 1}</PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem disabled={currentPage >= totalPages}>
                <PaginationLink onClick={() => setCurrentPage(totalPages)}>{tx("talepler.son")}</PaginationLink>
              </PaginationItem>
            </Pagination>
          </div>
        )}

        {!loading && records.length > 0 && (
          <div className="text-center text-muted small p-3">
            <p>{tx("talepler.sayfaToplamStand", { currentPage, totalPages, total })}</p>
          </div>
        )}
      </Container>
    </div>
  );
}
